import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { PublicTenant } from '../../shared/types';
import { queryKeys } from '../../lib/queryKeys';
import { apiRequest } from '../lib/api';
import { tenantSubdomainFromLocation } from '../lib/tenant';

interface TenantContextValue {
  tenant: PublicTenant | null;
  loading: boolean;
  error: string | null;
  reload(): Promise<void>;
}

const TenantContext = createContext<TenantContextValue | null>(null);

export function TenantProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const hasTenantHost = tenantSubdomainFromLocation() !== null;
  const tenantRequired = hasTenantHost && (location.pathname === '/' || location.pathname === '/login' || location.pathname === '/employee' || location.pathname.startsWith('/order/'));

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.tenant.public(),
    queryFn: () => apiRequest<PublicTenant>('/api/public/tenant', { auth: false }),
    enabled: tenantRequired,
    staleTime: 60_000,
  });

  const value = useMemo(() => ({
    tenant: tenantRequired ? data ?? null : null,
    loading: tenantRequired ? isLoading : false,
    error: !tenantRequired ? null : error instanceof Error ? error.message : null,
    reload: async () => {
      await refetch();
    },
  }), [tenantRequired, data, isLoading, error, refetch]);

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant(): TenantContextValue {
  const value = useContext(TenantContext);
  if (!value) throw new Error('useTenant debe usarse dentro de TenantProvider.');
  return value;
}
